/**
 * Gate — the visitor & security hub. Bero's differentiator surface promoted
 * to primary navigation (replaces the redundant Profile tab; Profile now lives
 * on the top-right avatar). Mobile: fixed BrandBar + scrolling content. Desktop
 * uses the sidebar; this renders capped inside it.
 *
 * "At the gate now" pulls the real pending-visitor list so the most urgent
 * action (approve a waiting delivery/guest) is the first thing here.
 */
import { router } from 'expo-router';
import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { BrandBar } from '@/components/shell/BrandBar';
import { str, useGateList } from '@/components/gate/api';
import { Icon, type IconName } from '@/features/account-admin/icons';
import { ArchSectionLabel } from '@/features/community/bits';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { useTheme } from '@/theme';
import { AppText, Button, Screen, Skeleton } from '@/ui';

type Pending = { id: string; name: string; purpose: string };

function toPending(raw: unknown): Pending | null {
  const id = str(raw, 'id');
  const name = str(raw, 'name', 'visitorName', 'fullName');
  if (!id || !name) return null;
  const purpose = str(raw, 'purpose', 'company', 'type') ?? 'Visitor';
  return { id, name, purpose };
}

const SERVICES: Array<{ icon: IconName; labelKey: DictKey; subKey: DictKey; path: string }> = [
  { icon: 'key', labelKey: 'gt_svc_gatepass', subKey: 'gt_svc_gatepass_sub', path: '/gatepass' },
  { icon: 'users', labelKey: 'gt_svc_party', subKey: 'gt_svc_party_sub', path: '/party-invite' },
  { icon: 'mail', labelKey: 'gt_svc_message', subKey: 'gt_svc_message_sub', path: '/message-guard' },
  { icon: 'clock', labelKey: 'gt_svc_history', subKey: 'gt_svc_history_sub', path: '/visitors' },
  { icon: 'users', labelKey: 'gt_svc_staff', subKey: 'gt_svc_staff_sub', path: '/staff' },
  { icon: 'check', labelKey: 'gt_svc_attendance', subKey: 'gt_svc_attendance_sub', path: '/attendance' },
  { icon: 'package', labelKey: 'gt_svc_parcels', subKey: 'gt_svc_parcels_sub', path: '/parcels' },
  { icon: 'shield', labelKey: 'gt_svc_delivery_pin', subKey: 'gt_svc_delivery_pin_sub', path: '/delivery-pin' },
  { icon: 'user-plus', labelKey: 'gt_svc_family', subKey: 'gt_svc_family_sub', path: '/family-pass' },
  { icon: 'user-plus', labelKey: 'gt_svc_kids', subKey: 'gt_svc_kids_sub', path: '/kids-permission' },
  { icon: 'building', labelKey: 'gt_svc_move', subKey: 'gt_svc_move_sub', path: '/movein-out' },
];

function ServiceTile({ icon, labelKey, subKey, path }: { icon: IconName; labelKey: DictKey; subKey: DictKey; path: string }) {
  const { colors } = useTheme();
  const { t } = useT();
  const label = t(labelKey);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={() => router.push(path)}
      style={({ pressed }) => ({
        // De-boxed (quiet-luxury pass): arch chip + text, no card border.
        width: '48%', borderRadius: radius.lg, paddingVertical: space.s3, paddingHorizontal: space.s2,
        backgroundColor: pressed ? colors.canvas1 : 'transparent', gap: 8,
      })}
    >
      <View style={{
        width: 38, height: 38, backgroundColor: colors.primary100,
        borderTopLeftRadius: 19, borderTopRightRadius: 19, borderBottomLeftRadius: 6, borderBottomRightRadius: 6,
        alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name={icon} size={18} color={colors.primary700} />
      </View>
      <View>
        <AppText variant="bodyMd" weight="600" numberOfLines={1}>{label}</AppText>
        <AppText variant="bodySm" tone="tertiary" numberOfLines={1}>{t(subKey)}</AppText>
      </View>
    </Pressable>
  );
}

export default function GateTab() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, state } = useGateList<unknown>('/api/visitors/pending', 'pending', 'items');
  const pending = items.map(toPending).filter((p): p is Pending => p !== null);

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <BrandBar title={t('tab_gate')} />
      <Screen padded scroll style={{ paddingTop: space.s4 }}>
        {/* At the gate now */}
        <ArchSectionLabel>{t('gt_at_gate_now')}</ArchSectionLabel>
        {state === 'loading' ? (
          <Skeleton h={72} />
        ) : pending.length === 0 ? (
          <View style={{
            borderRadius: radius.lg,
            backgroundColor: colors.canvas1, padding: space.s4, flexDirection: 'row', alignItems: 'center', gap: space.s3,
          }}>
            <View style={{ width: 40, height: 40, borderRadius: radius.full, backgroundColor: colors.success50, alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="check" size={18} color={colors.success700} />
            </View>
            <View style={{ flex: 1 }}>
              <AppText variant="bodyMd" weight="500">{t('gt_none_waiting')}</AppText>
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{t('gt_none_waiting_sub')}</AppText>
            </View>
          </View>
        ) : (
          <View style={{ gap: space.s2 }}>
            {pending.map((p) => (
              <Pressable
                key={p.id}
                accessibilityRole="button"
                onPress={() => router.push(`/visitor-approval?id=${p.id}`)}
                style={({ pressed }) => ({
                  borderRadius: radius.lg, borderWidth: 1, borderColor: colors.danger500,
                  backgroundColor: pressed ? colors.danger50 : colors.canvas0,
                  padding: space.s4, flexDirection: 'row', alignItems: 'center', gap: space.s3,
                })}
              >
                <View style={{
                  width: 44, height: 48, borderTopLeftRadius: 22, borderTopRightRadius: 22,
                  borderBottomLeftRadius: 6, borderBottomRightRadius: 6,
                  backgroundColor: colors.danger50, alignItems: 'center', justifyContent: 'center',
                }}>
                  <AppText weight="700" style={{ color: colors.danger700 }}>
                    {p.name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                  </AppText>
                </View>
                <View style={{ flex: 1 }}>
                  <AppText variant="bodyMd" weight="600">{p.name}</AppText>
                  <AppText variant="bodySm" tone="secondary" style={{ marginTop: 2 }}>{p.purpose} · {t('gt_tap_review')}</AppText>
                </View>
                <AppText variant="bodyLg" tone="tertiary">›</AppText>
              </Pressable>
            ))}
          </View>
        )}

        <Button
          title={t('gt_preapprove')}
          size="lg"
          onPress={() => router.push('/preapprove')}
          style={{ marginTop: space.s5 }}
        />

        <View style={{ marginTop: space.s8 }}>
          <ArchSectionLabel>{t('gt_gate_security')}</ArchSectionLabel>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', rowGap: space.s3 }}>
            {SERVICES.map((s) => <ServiceTile key={s.labelKey} {...s} />)}
          </View>
        </View>
      </Screen>
    </View>
  );
}
